import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from "wouter";

import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";

import { absURL } from '../utils';
import QuizApi from '../QuizApi';
import AdminHeader from './AdminHeader';
import InfoTooltip from './InfoTooltip';

import Modal from "react-bootstrap/Modal";
import DeezerSearch from './DeezerSearch';

export default function AdminQuizEdit(props) {

  const { quizId } = props;

  const [quizName, setQuizName] = useState("");
  const [quizType, setQuizType] = useState("");
  const [quizDurationMinutes, setQuizDurationMinutes] = useState(null);
  const [liveInfo, setLiveInfo] = useState(null);

  const [horizontalScroll, setHorizontalScroll] = useState(false);
  const [freeTextResultMode, setFreeTextResultMode] = useState("fadeOut");
  const [singleQuestionMode, setSingleQuestionMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [, setLocation] = useLocation();
  const [displayError, setDisplayError] = useState(false);
  const [submitErrorMsg, setSubmitErrorMsg] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quizGroups, setQuizGroups] = useState([]);
  const [quizGroupId, setQuizGroupId] = useState(null);
  const [lobbyMusic, setLobbyMusic] = useState(null);
  const [competitionMode, setCompetitionMode] = useState(false);
  const [simCount, setSimCount] = useState(10);
  const [simulating, setSimulating] = useState(false);

  // =============================
  // Effect to fetch quiz details
  // =============================
  useEffect(() => {

    const fn = async () => {

      const [detailsResult, groupsResult] = await Promise.all([
        QuizApi.quizDetails(quizId),
        QuizApi.quizGroupList()
      ]);

      setQuizGroups(groupsResult?.response?.groups ?? []);

      if (detailsResult && detailsResult.success && detailsResult.response && detailsResult.response.quiz) {

        const quiz = detailsResult.response.quiz;

        setQuizName(quiz.name);
        setQuizType(quiz.type);
        setQuizDurationMinutes(quiz.durationSeconds / 60);
        setQuizGroupId(quiz.quizGroupId || null);
        setLiveInfo({
          liveURL: quiz.liveURL,
          liveAdminURL: quiz.liveAdminURL,
          liveAdminURLQrCode: quiz.liveAdminURLQrCode,
          liveURLQrCode: quiz.liveURLQrCode
        });

        if (quiz.config) {
          setHorizontalScroll(!!quiz.config.horizontalScroll);
          if (quiz.config.freeTextResultMode === "keepAll") {
            setFreeTextResultMode("keepAll");
          }
          setSingleQuestionMode(!!quiz.config.singleQuestionMode);
          setCompetitionMode(!!quiz.config.competitionMode);
          setLobbyMusic(quiz.config.lobbyMusic || null);
        }

      }

      setLoading(false);

    };

    fn();

  }, [quizId]);

  // =======================
  // Form submit callback
  // =======================
  const onSubmit = useCallback(ev => {

    ev.preventDefault();

    const fn = async () => {

      if (!(quizName || "").trim()) {
        setDisplayError(true);
        return;
      }

      setLoading(true);

      const quizConfig = {
        horizontalScroll,
        ...(quizType === "Free" ? { freeTextResultMode } : {}),
        ...(quizType === "Quiz" ? { singleQuestionMode, competitionMode } : {}),
        lobbyMusic: lobbyMusic || null
      };

      let result = null;
      try
      {
        result = await QuizApi.quizEdit(quizId, quizName, quizDurationMinutes, quizConfig, quizGroupId);
      } catch(e) {
        setDisplayError(true);
        setSubmitErrorMsg(e.message);
      }

      setLoading(false);

      if (result && result.success) {
        setLocation(absURL(`/admin`));
      }
      else {
        setDisplayError(true);
        setSubmitErrorMsg("Det uppstod ett fel när vi försökte skapa quizzen.");
      }

    };

    fn();

  }, [quizId, quizName, quizType, quizDurationMinutes, horizontalScroll, freeTextResultMode, singleQuestionMode, competitionMode, setLocation, quizGroupId, lobbyMusic])

  const runSimulation = useCallback(async () => {
    setSimulating(true);
    const win = window.open('', '_blank');
    const result = await QuizApi.quizSimulate(quizId, simCount);
    setSimulating(false);
    if (result && result.success && result.response.defaultAccessId) {
      win.location.href = absURL(`/${result.response.defaultAccessId}/result`);
    } else if (win) {
      win.close();
    }
  }, [quizId, simCount]);

  const handleDeletePerform = useCallback(() => {
    setShowDeleteModal(false);
    const fn = async () => {
      setLoading(true);
      await QuizApi.quizDelete(quizId);
      setLocation(absURL(`/admin`));
    };
    fn();
  }, [quizId, setLocation]);

  return (
		<div id="adminQuizCreate">
			<div>

        {loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && 
          <div>
            <AdminHeader />
            <div className="adminContentWrapper">

              <Button variant="outline-secondary" size="sm" onClick={() => window.history.back()}>
                Tillbaka
              </Button>

              <form onSubmit={onSubmit} >

                <div className="form-group mt-3">
                  <label htmlFor="quizName">Namn:</label>
                  <input
                    type="text"
                    id="quizName"
                    className="form-control"
                    value={quizName}
                    onChange={ev => setQuizName(ev.target.value)} />
                </div>

                <div className="form-group mt-2">
                  <label htmlFor="quizGroup">Grupp:</label>
                  <select
                    id="quizGroup"
                    className="form-control"
                    value={quizGroupId || ""}
                    onChange={ev => setQuizGroupId(ev.target.value || null)}>
                    <option value="">Ingen grupp</option>
                    {quizGroups.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>

                {displayError && !(quizName || "").trim() && <Alert variant={"danger"}>
                  Du måste ange ett quiznamn
                </Alert>
                }

                <div className="form-group">
                  <label for="quizName">Tid (minuter):</label>
                  <input 
                    type="number" 
                    id="quizName"
                    class="form-control" 
                    value={quizDurationMinutes}
                    onChange={ev => setQuizDurationMinutes(ev.target.value)} />
                </div>

                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    name="horizontalScroll"
                    id="horizontalScroll"
                    checked={horizontalScroll}
                    onChange={ev => setHorizontalScroll(ev.target.checked)}
                    />
                  <label class="form-check-label" for="horizontalScroll">
                    Horisontell scroll (swipe)
                  </label>
                </div>

                {quizType === "Free" &&
                  <div className="form-group mt-3">
                    <label>Visning av fritext-svar:</label>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="freeTextResultMode"
                        id="freeTextResultModeFadeOut"
                        value="fadeOut"
                        checked={freeTextResultMode === "fadeOut"}
                        onChange={() => setFreeTextResultMode("fadeOut")}
                      />
                      <label class="form-check-label" for="freeTextResultModeFadeOut">
                        Tona ut svar (standard)
                      </label>
                    </div>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="freeTextResultMode"
                        id="freeTextResultModeKeepAll"
                        value="keepAll"
                        checked={freeTextResultMode === "keepAll"}
                        onChange={() => setFreeTextResultMode("keepAll")}
                      />
                      <label class="form-check-label" for="freeTextResultModeKeepAll">
                        Behåll alla svar (scrollbar lista)
                      </label>
                    </div>
                  </div>
                }

                {quizType === "Quiz" &&
                  <div class="form-check mt-2">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      name="singleQuestionMode"
                      id="singleQuestionMode"
                      checked={singleQuestionMode}
                      onChange={ev => setSingleQuestionMode(ev.target.checked)}
                    />
                    <label class="form-check-label" for="singleQuestionMode">
                      Visa resultat som fördelningsdiagram
                      <InfoTooltip placement="left" className="ml-1">
                        <p>Istället för "% rätt per fråga" visas en stapel per svarsalternativ med markering av rätt svar — bra för att se hur gruppen resonerade.</p>
                      </InfoTooltip>
                    </label>
                  </div>
                }

                {quizType === "Quiz" &&
                  <div class="form-check mt-2">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      name="competitionMode"
                      id="competitionMode"
                      checked={competitionMode}
                      onChange={ev => setCompetitionMode(ev.target.checked)}
                    />
                    <label class="form-check-label" for="competitionMode">
                      Tävlingsläge
                      <InfoTooltip placement="left" className="ml-1">
                        <p>Resultatsidan visar en dynamisk topplista med alla deltagares poäng i realtid, sorterad från bäst till sämst.</p>
                      </InfoTooltip>
                    </label>
                  </div>
                }

                <div className="mt-3">
                  <label>Lobbymusik (spelas medan deltagarna väntar):</label>
                  {lobbyMusic && (
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      className="ml-2"
                      onClick={() => setLobbyMusic(null)}>
                      Ta bort lobbymusik
                    </Button>
                  )}
                  <DeezerSearch
                    label=""
                    selectedTrack={lobbyMusic}
                    onTrackSelected={track => setLobbyMusic({
                      trackId: track.id,
                      previewUrl: track.previewUrl,
                      title: track.title,
                      artist: track.artist,
                      album: track.album,
                      cover: track.cover
                    })}
                  />
                </div>

<div className="d-flex align-items-center mt-4" style={{ gap: "12px" }}>
  <Button type="submit" className="btn btn-quizPrimaryDark">Spara ändringar</Button>
  <Link href={absURL(`/admin/quiz/${quizId}/questions`)}>
    <Button variant="outline-secondary">Frågor</Button>
  </Link>
  <Button
    variant="outline-danger"
    onClick={() => setShowDeleteModal(true)}>
    Ta bort quiz
  </Button>
</div>

                <div className="d-flex align-items-center mt-3" style={{ gap: "8px" }}>
                  <label className="mb-0 small text-muted" htmlFor="simCount">Test-deltagare:</label>
                  <input
                    id="simCount"
                    type="number"
                    min="1"
                    max="200"
                    className="form-control form-control-sm"
                    style={{ width: "70px" }}
                    value={simCount}
                    onChange={e => setSimCount(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    disabled={simulating}
                    onClick={runSimulation}>
                    {simulating ? '...' : 'Test'}
                  </Button>
                </div>
                {displayError && submitErrorMsg && 
                  <Alert variant={"danger"} className="mt-4">
                    {submitErrorMsg}
                  </Alert>
                }

              </form>

            </div>

<Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
  <Modal.Header closeButton>
    <Modal.Title>Ta bort quiz</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    Vill du verkligen ta bort <strong>{quizName}</strong>? Detta går inte att ångra.
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Avbryt</Button>
    <Button variant="danger" onClick={handleDeletePerform}>Ta bort</Button>
  </Modal.Footer>
</Modal>

            {!!liveInfo && <div className="d-flex flex-column w-100 mt-4">

              <div className="">
                <h6>Deltagarlänk</h6>
                <small className="d-inline overflow-hidden mt-2 px-2 quizUrlPill">
                  <a target="_blank" rel="noreferrer" className="text-quizPrimaryBold" href={liveInfo.liveURL}>{liveInfo.liveURL}</a>
                </small>
              </div>

              <div className="mt-2">
                <img src={liveInfo.liveURLQrCode} 
                  className="border rounded" 
                  style={{ width: "120px" }}
                  alt="QR-kod för deltagarlänk" />  
              </div>

              <div className="mt-4">
                <h6>Adminlänk (kräver inlogg)</h6>
                <small className="d-inline overflow-hidden mt-2 px-2 quizUrlPill">
                  <a target="_blank" rel="noreferrer" className="text-quizPrimaryBold" href={liveInfo.liveAdminURL}>{liveInfo.liveAdminURL}</a>
                </small>
              </div>

              <div className="mt-2">
                <img 
                  src={liveInfo.liveAdminURLQrCode} 
                  className="border rounded" 
                  style={{width: "120px"}}
                  />
              </div>

            </div>}

          </div>
        }
      </div>
    </div>
  )
};